import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, InputGroup, Table } from 'react-bootstrap';
import { getOwnerFinanceOverrides, listOwners, updateOwnerFinanceOverrides } from '../api/financeApi';
import InfoTip from '../components/InfoTip';
import { useFinance } from '../financeContext';
import { OWNER_OVERRIDE_FIELDS, OWNER_FINANCE_MODULES, VERTICALS, VERTICAL_LABEL } from '../wording';

const rate = (v) => (v == null || v === '' ? '—' : `${(Number(v) * 100).toFixed(v * 100 % 1 ? 1 : 0)}%`);
const money = (v) => (v == null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);

export default function OwnerOverridesPage() {
  const { canEditConfig } = useFinance();

  // ---- owner picker ----
  const [withOverrides, setWithOverrides] = useState([]);       // owners that already have terms
  const [listErr, setListErr] = useState('');
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);                   // typeahead results
  const [showMatches, setShowMatches] = useState(false);
  const searchSeq = useRef(0);

  // ---- selected owner + editor ----
  const [selected, setSelected] = useState(null);               // { _id, name, email }
  const [overrides, setOverrides] = useState(null);
  const [ambiguous, setAmbiguous] = useState(null);             // candidate list on a 409
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const loadList = () => {
    listOwners({ withOverrides: true })
      .then(setWithOverrides)
      .catch(() => setListErr('Could not load the list of owners with negotiated terms.'));
  };
  useEffect(loadList, []);

  // debounced typeahead
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setMatches([]); return; }
    const seq = ++searchSeq.current;
    const t = setTimeout(async () => {
      try {
        const rows = await listOwners({ q });
        if (seq === searchSeq.current) { setMatches(rows); setShowMatches(true); }
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const loadOwner = async (ownerRef, display) => {
    setErr(''); setMsg(''); setAmbiguous(null); setShowMatches(false);
    try {
      const res = await getOwnerFinanceOverrides(ownerRef);
      setSelected(res?.owner || display || { _id: ownerRef, name: ownerRef, email: '' });
      setOverrides(res?.data || {});
    } catch (e) {
      setOverrides(null); setSelected(null);
      if (e?.response?.status === 409 && e.response.data?.owners) {
        setAmbiguous(e.response.data.owners);
        setErr(e.response.data.msg || 'More than one owner matches that name.');
      } else {
        setErr(e?.response?.data?.msg || 'Owner not found.');
      }
    }
  };

  const lookup = () => { if (query.trim()) loadOwner(query.trim()); };

  const setField = (key, v) => setOverrides((p) => ({ ...(p || {}), [key]: v === '' ? null : v }));

  const save = async (e) => {
    e.preventDefault();
    setErr(''); setMsg(''); setSaving(true);
    try {
      const payload = { notes: overrides.notes || null };
      OWNER_OVERRIDE_FIELDS.forEach((f) => {
        const v = overrides[f.key];
        payload[f.key] = v === '' || v == null ? null : Number(v);
      });
      payload.allowedFinanceModules = overrides.allowedFinanceModules || [];
      payload.allowedVerticals = overrides.allowedVerticals || [];
      const res = await updateOwnerFinanceOverrides(selected._id, payload);
      setOverrides(res?.data || {});
      if (res?.owner) setSelected(res.owner);
      setMsg("Owner's finance overrides saved.");
      loadList();
    } catch (e2) {
      setErr(e2?.response?.data?.msg || 'Unable to update finance overrides.');
    } finally {
      setSaving(false);
    }
  };

  const listedIds = useMemo(() => new Set(withOverrides.map((o) => o._id)), [withOverrides]);

  return (
    <div style={{ maxWidth: 640 }}>
      <h5>Owner-specific finance overrides</h5>
      <p className="text-muted small">
        Per-owner negotiated rates. Anything left blank falls back to the platform default from
        Settlement configuration.
      </p>

      {/* ---- owners that already have negotiated terms ---- */}
      {listErr && <Alert variant="warning" className="py-2">{listErr}</Alert>}
      <h6 className="mt-3">Owners with negotiated terms</h6>
      {withOverrides.length === 0 ? (
        <p className="text-muted small">No owner has a negotiated override yet — look one up below to add the first.</p>
      ) : (
        <Table hover size="sm" responsive className="align-middle">
          <thead>
            <tr>
              <th>Owner</th><th>Commission</th><th>Platform fee</th><th>Finance-lead cap</th><th>Max forfeit</th><th />
            </tr>
          </thead>
          <tbody>
            {withOverrides.map((o) => (
              <tr key={o._id}>
                <td>
                  {o.name}
                  <div className="text-muted small">{o.email}</div>
                </td>
                <td>{rate(o.financeOverrides?.commissionRate)}</td>
                <td>{rate(o.financeOverrides?.platformFeeRate)}</td>
                <td>{money(o.financeOverrides?.financeLeadApprovalThreshold)}</td>
                <td>{rate(o.financeOverrides?.cancellationMaxForfeitRate)}</td>
                <td className="text-end">
                  <Button size="sm" variant={selected?._id === o._id ? 'primary' : 'outline-primary'} onClick={() => loadOwner(o._id, o)}>
                    {selected?._id === o._id ? 'Editing' : 'Edit'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* ---- look up any owner (id / email / name) ---- */}
      <h6 className="mt-4">Look up an owner</h6>
      <div className="position-relative mb-2" style={{ maxWidth: 420 }}>
        <InputGroup size="sm">
          <Form.Control
            placeholder="Owner name, email or id"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => matches.length && setShowMatches(true)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), lookup())}
          />
          <Button variant="outline-secondary" onClick={lookup}>Look up</Button>
        </InputGroup>
        {showMatches && matches.length > 0 && (
          <div className="border rounded bg-white shadow-sm position-absolute w-100" style={{ zIndex: 5, maxHeight: 240, overflowY: 'auto' }}>
            {matches.map((o) => (
              <button
                type="button"
                key={o._id}
                className="btn btn-link text-decoration-none text-start d-block w-100 px-2 py-1 text-body"
                onClick={() => { setQuery(o.name); loadOwner(o._id, o); }}
              >
                {o.name} <span className="text-muted small">{o.email}</span>
                {listedIds.has(o._id) && <Badge bg="info" className="ms-1">has terms</Badge>}
              </button>
            ))}
          </div>
        )}
      </div>

      {err && <Alert variant="danger" className="py-2">{err}</Alert>}
      {msg && <Alert variant="success" className="py-2">{msg}</Alert>}

      {ambiguous && (
        <div className="mb-3">
          <p className="small mb-1">Pick the owner you meant:</p>
          {ambiguous.map((o) => (
            <Button key={o._id} size="sm" variant="outline-secondary" className="me-2 mb-2" onClick={() => loadOwner(o._id, o)}>
              {o.name} · {o.email}
            </Button>
          ))}
        </div>
      )}

      {overrides && selected && (
        <Form onSubmit={save} className="mt-3">
          <h6>
            {selected.name}
            {selected.email && <span className="text-muted small ms-2">{selected.email}</span>}
          </h6>
          {!canEditConfig && <Alert variant="light" className="py-2 small">View-only — only an admin or finance manager can save changes.</Alert>}
          {OWNER_OVERRIDE_FIELDS.map((f) => (
            <Form.Group className="mb-3" key={f.key}>
              <Form.Label className="mb-1">{f.label} <InfoTip text={f.help} /></Form.Label>
              <InputGroup size="sm" style={{ maxWidth: 320 }}>
                <Form.Control
                  type="number"
                  step={f.kind === 'rate' ? '0.01' : '1'}
                  min="0"
                  max={f.kind === 'rate' ? '1' : undefined}
                  disabled={!canEditConfig}
                  value={overrides[f.key] ?? ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
                {f.unit && <InputGroup.Text>{f.unit}</InputGroup.Text>}
              </InputGroup>
              <Form.Text muted>Blank = platform default. {f.help}</Form.Text>
            </Form.Group>
          ))}
          <Form.Group className="mb-3">
            <Form.Label className="mb-1">Always show these finance tabs <InfoTip text="If a module is ticked, the owner sees that tab even with nothing outstanding. Unticked tabs appear only when they have unpaid items." /></Form.Label>
            {OWNER_FINANCE_MODULES.map((m) => (
              <Form.Check
                key={m.key}
                type="checkbox"
                id={`fin-mod-${m.key}`}
                label={m.label}
                disabled={!canEditConfig}
                checked={(overrides.allowedFinanceModules || []).includes(m.key)}
                onChange={() => {
                  const cur = overrides.allowedFinanceModules || [];
                  const next = cur.includes(m.key) ? cur.filter((x) => x !== m.key) : [...cur, m.key];
                  setField('allowedFinanceModules', next);
                }}
              />
            ))}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-1">Extra verticals (admin) <InfoTip text="Leave blank for the owner’s own business app only. Tick extra verticals only if this owner is allowed to see finance for other businesses." /></Form.Label>
            {VERTICALS.map((v) => (
              <Form.Check
                key={v}
                type="checkbox"
                id={`fin-vert-${v}`}
                label={VERTICAL_LABEL[v]}
                disabled={!canEditConfig}
                checked={(overrides.allowedVerticals || []).includes(v)}
                onChange={() => {
                  const cur = overrides.allowedVerticals || [];
                  const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
                  setField('allowedVerticals', next);
                }}
              />
            ))}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-1">Negotiation notes</Form.Label>
            <Form.Control as="textarea" rows={2} disabled={!canEditConfig}
              value={overrides.notes || ''} onChange={(e) => setField('notes', e.target.value)} />
          </Form.Group>
          {canEditConfig && <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save overrides'}</Button>}
        </Form>
      )}
    </div>
  );
}
